'use client'

import { useEffect, useState } from 'react'
import { TrendingUp, Flame, Eye, ArrowRight } from 'lucide-react'
import { getHsCodeListDetail } from '@/modules/hs/api/homepage.api'
import { TrendingHsCode } from '@/modules/hs/types/homepage.types'
import TaxHistoryChart from './TaxHistoryChart'
import Link from '@/modules/shared/components/Link'

const HS_CODE_LIST = ['73269099', '39269099', '85371019', '85371099', '90318090']

export default function FtaSection() {
  const [trendingHsCodes, setTrendingHsCodes] = useState<TrendingHsCode[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const randomHsCode =
    HS_CODE_LIST[Math.floor(Math.random() * HS_CODE_LIST.length)]

  useEffect(() => {
    async function fetchTrending() {
      try {
        const data = await getHsCodeListDetail(HS_CODE_LIST)
        setTrendingHsCodes(data)
      } catch (error) {
        console.error('Failed to fetch trending HS codes:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchTrending()
  }, [])

  const formatTaxRate = (rate: number | null) => {
    if (rate === null || rate < 0) return 'N/A'
    return `${rate}%`
  }

  return (
    <section className="py-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Chart */}
        <div className="cl-card-border">
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-[#e5e5e5]">
            <div
              className="flex items-center gap-2 text-lg font-semibold text-[#171717] "
            >
              <TrendingUp size={20} className="text-[#1c5caa]" />
              Diễn biến thuế suất cho mã HS
              <Link href={`/chi-tiet-ma-hs/${randomHsCode}`} className='cl-hs-code'>{randomHsCode || '...'}</Link>
            </div>
            <a href="#search-hs" className="fta-link">
              Tìm mã khác
              <ArrowRight size={14} />
            </a>
          </div>
          <div className="chart-wrapper">
            <TaxHistoryChart hsCode={randomHsCode} />
          </div>
        </div>

        {/* Right: Trending HS Codes */}
        <div className="cl-card-border">
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-[#e5e5e5]">
            <div className="flex items-center gap-2 text-lg font-bold text-[#171717]">
              <Flame size={20} className="text-[#1c5caa]" />
              Mã HS xem nhiều
            </div>
            <a href="#search-hs" className="fta-link">
              Xem tất cả
              <ArrowRight size={14} />
            </a>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-sm text-[#737373]">Đang tải...</div>
            </div>
          ) : trendingHsCodes.length === 0 ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-sm text-[#737373]">
                Không có dữ liệu mã HS
              </div>
            </div>
          ) : (
            <ul className="flex flex-col gap-6">
              {trendingHsCodes.map((item, index) => (
                <Link
                  href={`/chi-tiet-ma-hs/${item.hsCode}`}
                  key={item.hsCode}
                  className="flex gap-4 items-start p-4 rounded-lg transition-colors hover:bg-[#f9fafb] cursor-pointer border-b border-[#e5e5e5]"
                >
                  <div className="w-8 h-8 bg-linear-to-br from-[#1c5caa] to-[#4d86ce] text-white rounded-full flex items-center justify-center font-bold shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                      <div className="text-lg font-semibold">
                        {item.hsCode}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {/*NKTT */}
                        {item.taxes.nktt !== null && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold pro">
                            NKTT : {formatTaxRate(item.taxes.nktt)}
                          </span>
                        )}
                        {/* MFN Tax (NKTT) */}
                        {item.taxes.nkud !== null && (
                          <span className="tax-badge mfn inline-flex items-center gap-1">
                            MFN: {formatTaxRate(item.taxes.nkud)}
                          </span>
                        )}
                        {/* FTA Tax (NKUDDB) */}
                        {item.taxes.nkuddb && (
                          <span className="tax-badge fta inline-flex items-center gap-1">
                            <Flame size={12} />
                            {formatTaxRate(item.taxes.nkuddb.rate)} (
                            {item.taxes.nkuddb.agreement})
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-[#171717] mb-2">
                      {item.description}
                    </div>
                  </div>
                </Link>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  )
}
